import { useEffect } from 'react';
import { FAIcon, IconStyle, Theme } from '@nextgen-web-framework/all';
import { useSelector } from 'react-redux';
import type { AppState } from '@nextgen-web-framework/core';
import {
  Citizenship,
  type AlternativeContactType,
  type DropDownData as Data,
} from '@commerce-profile-webcomponents/sdk';
import { iff, parsePhoneNumber, parseString, siteCountry } from '../../../utils/helper';
import {
  getDropdownValueByOptions,
  toTitleCaseLabel,
  ProfileSectionConst,
  getExt,
} from '../../../utils/myAccountProfileUtils';
import { useMyAccount } from '../../../utils/myAccountHook';
import type { MyAccountInput, MyAccountOption } from '../../../utils/types/propTypes';
import { MyAccountExtEnum, MyAccountFieldType, MyAccountFieldName } from '../../../utils/types/enum';
import { useAddressFormat } from '../../../utils/myAccountAddressUtils';
import { SponsorCard } from '../../RegistrationSponsor/SponsorCard/SponsorCard';
import { DisplayField } from '../../Common/DisplayField/DisplayField';
import { AccountInformationViewProps } from './types/propTypes';
import styles from './AccountInformationView.module.scss';

const AccountInformationView = ({ masterDataConfig, sliceName, removeBorder = false }: AccountInformationViewProps) => {
  const addrFormat = useAddressFormat;
  const { t, breakpoint, getDateLocale, getLabel, setConfig, isAdditionalPhoneEnabled } = useMyAccount();

  const { [sliceName]: profileInformation } = useSelector((state: AppState) => state.commerceProfile);
  const { profileData } = profileInformation;
  const { dialCode = '', phone = '', email = '' } = profileData;

  const dateLocale = getDateLocale(profileData);
  const siteCountryCode = siteCountry().toLowerCase();

  const { inputs: configList } = masterDataConfig;

  useEffect(() => {
    setConfig(masterDataConfig);
  }, []);

  // To reduce sonar cognitive complexity
  const renderAddress = (sectionItem: MyAccountInput) => {
    const { section, inputs, ext } = sectionItem;

    const removeBorderConfig = (getExt(MyAccountExtEnum.UI_REMOVE_BORDER_BOTTOM, ext) as string[]) ?? [];
    const removeBorderBottom = removeBorderConfig.includes(breakpoint);

    const numberOfVisible = inputs?.filter((input: MyAccountInput) => input.visible).length;
    const fullWidthClassName = numberOfVisible === 1 ? styles['full-width'] : '';

    return (
      <div
        key={`section-${section}`}
        className={`${styles['field-row']} ${styles['address']} ${fullWidthClassName} ${
          removeBorderBottom ? styles['remove-border-bottom'] : ''
        }`}
      >
        {inputs
          ?.filter((inputItem: MyAccountInput) => inputItem?.visible)
          ?.map((inputItem: MyAccountInput) => {
            const { fieldName } = inputItem;
            const label = getLabel(inputItem, true);

            if (
              [MyAccountFieldName.MAILING_ADDRESS, MyAccountFieldName.BILLING_ADDRESS].includes(
                fieldName as MyAccountFieldName,
              )
            ) {
              const formattedAddr = addrFormat(profileInformation?.[fieldName], true);
              return <DisplayField key={fieldName} label={label} value={formattedAddr} />;
            }
            return <></>;
          })}
      </div>
    );
  };

  const renderTax = (sectionItem: MyAccountInput) => {
    const { section, inputs, ext } = sectionItem;

    const columns = (getExt(MyAccountExtEnum.UI_COLUMNS, ext) as number) ?? 0;
    const columnClass = iff(columns >= 2, styles['two-cols'], '');

    return (
      <div key={section} className={`${styles['field-row']} ${styles['address']} ${styles['tax']} ${columnClass}`}>
        {inputs
          ?.filter((inputItem: MyAccountInput) => inputItem?.visible)
          ?.map((inputItem: MyAccountInput) => {
            const { fieldName, fieldType, options } = inputItem;

            let value = profileData?.[fieldName];
            let label = getLabel(inputItem);

            if (fieldName === MyAccountFieldName.TAX_ADDRESS) {
              value = addrFormat(profileInformation?.[fieldName], true);
              label = t(`profile.AddressInput.${fieldName}`);
            } else if (fieldType === MyAccountFieldType.DROPDOWN) {
              value = processDropdownValue(fieldName, options as unknown as Data[], inputItem?.ext ?? []);
            }
            return <DisplayField key={fieldName} label={label} value={value} />;
          })}
      </div>
    );
  };

  const processDropdownValue = (fieldName: string, options: Data[], ext: MyAccountOption[]) => {
    let labelPrefix = '';
    const fieldValue = profileData?.[fieldName];

    if (fieldName === MyAccountFieldName.CITIZENSHIP) {
      labelPrefix = siteCountryCode.toUpperCase();

      if (profileData?.[fieldName] !== Citizenship.Citizen) {
        const citizenshipCountryField = String(getExt(MyAccountExtEnum.DISPLAY_CITIZENSHIP_COUNTRY_FIELD, ext));
        const citizenshipValue = profileData?.[citizenshipCountryField];
        if (citizenshipCountryField && citizenshipValue) {
          return t(`profile.RegistrationIdVerificationStep.country.${citizenshipValue}`);
        }
      }
    }

    return getDropdownValueByOptions(t, fieldName, fieldValue, options as unknown as Data[], labelPrefix);
  };

  const renderProfileInfo = (sectionItem: MyAccountInput) => {
    const { section, inputs } = sectionItem;
    return (
      <div key={section} className={`${styles['field-row']} ${styles['information']}`}>
        {inputs
          ?.filter((inputItem: MyAccountInput) => inputItem?.visible)
          ?.map((inputItem: MyAccountInput) => {
            const { fieldName, options, fieldType } = inputItem;
            let label = getLabel(inputItem);

            if (fieldName === MyAccountFieldName.PROFILE_PICTURE) {
              return (
                <div key={fieldName} className={`${styles['span-col']} ${styles['profile-picture-container']}`}>
                  <div className={styles['image-wrapper']}>
                    <FAIcon
                      iconName="circle-user"
                      className={styles['profile-picture']}
                      iconStyle={IconStyle.SOLID}
                      theme={Theme.PURPLE}
                    />
                  </div>
                </div>
              );
            } else if (options && options.length > 0) {
              const value = processDropdownValue(fieldName, options as unknown as Data[], inputItem?.ext ?? []);

              return <DisplayField key={fieldName} label={label} value={value} />;
            } else if (fieldType === MyAccountFieldType.SPONSOR) {
              const sponsorCountry = profileData?.sponsorLocation
                ? t(`profile.RegistrationIdVerificationStep.country.${profileData?.sponsorLocation}`)
                : '';
              const aboData = {
                accountNumber: profileData?.sponsorNumber,
                accountName: profileData?.sponsorName,
                location: sponsorCountry,
              };

              return (
                <div key={fieldName} className={`${styles['span-col']}`}>
                  <DisplayField
                    key={fieldName}
                    label={label}
                    content={<SponsorCard abo={aboData} isDisplay={true} />}
                  />
                </div>
              );
            } else {
              if (fieldType === MyAccountFieldType.DATE && profileData?.citizenship !== Citizenship.Citizen) {
                label = t(`profile.ProfileInfo.${fieldName}`);
              } else if (
                fieldName === MyAccountFieldName.ID_NUMBER &&
                profileData?.citizenship !== Citizenship.Citizen
              ) {
                label = t(`profile.ProfileInfo.idNumber.foreigner`);
              }

              return (
                <DisplayField
                  key={fieldName}
                  label={label}
                  value={profileData?.[fieldName]}
                  type={parseString(fieldType) as MyAccountFieldType}
                  dateLocale={dateLocale}
                />
              );
            }
          })}
      </div>
    );
  };

  const renderContacts = (sectionItem: MyAccountInput) => {
    const { section, inputs, ext } = sectionItem;

    let className = '';
    const columns = (getExt(MyAccountExtEnum.UI_FULL_WIDTH, ext) as boolean) ?? false;
    className = iff(columns, styles['span-col'], '');

    const separator = (getExt(MyAccountExtEnum.UI_SEPARATOR, ext) as boolean) ?? false;
    className = iff(separator, `${className} ${styles['use-separator']}`, className);

    return (
      <div key={section} className={`${styles['field-row']} ${styles['contact']}`}>
        {inputs
          ?.filter((inputItem: MyAccountInput) => inputItem?.visible)
          ?.map((inputItem: MyAccountInput) => {
            const { fieldName, fieldType, footerNote } = inputItem;
            const label = getLabel(inputItem);

            let value = email;
            let displayType = MyAccountFieldType.NONE;
            if (fieldType === MyAccountFieldName.PHONE) {
              value = dialCode && phone ? parsePhoneNumber(dialCode, phone) : '';
              displayType = MyAccountFieldType.PHONE;
            }

            let footerTxt = '';
            if (footerNote) {
              footerTxt = t(footerNote as string);
            }
            return (
              <div key={fieldName} className={className}>
                <DisplayField key={fieldName} label={label} value={value} type={displayType} footer={footerTxt} />
              </div>
            );
          })}

        {isAdditionalPhoneEnabled() &&
          profileData?.alternativePhone
            ?.filter(({ isDeleted }: AlternativeContactType) => !isDeleted)
            ?.map((altContact: AlternativeContactType) => {
              const { dialCode = '', number = '', type = '', id } = altContact;
              return (
                <DisplayField
                  label={t(`profile.ProfileInfo.alternativeNumber.${toTitleCaseLabel(type)}`)}
                  value={parsePhoneNumber(dialCode, number)}
                  key={id}
                  type={MyAccountFieldType.PHONE}
                />
              );
            })}
      </div>
    );
  };

  return (
    <section
      className={`${styles['profile-info-container']} ${iff(removeBorder, styles['remove-border'], '')}`}
      data-testid="profile-info-view-container-testid"
    >
      {configList?.map((sectionItem: MyAccountInput) => {
        const { section } = sectionItem;
        switch (section) {
          case ProfileSectionConst.PROFILE_SECTION_ADDRESS: {
            return renderAddress(sectionItem);
          }
          case ProfileSectionConst.PROFILE_SECTION_TAX: {
            if (profileData.citizenship === Citizenship.Citizen) {
              return renderTax(sectionItem);
            }
            return <></>;
          }
          case ProfileSectionConst.PROFILE_SECTION_INFO: {
            return renderProfileInfo(sectionItem);
          }
          case ProfileSectionConst.PROFILE_SECTION_CONTACT: {
            return renderContacts(sectionItem);
          }
          default:
            return renderProfileInfo(sectionItem);
        }
      })}
    </section>
  );
};

export default AccountInformationView;
